import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Seo from "@/components/Seo";
import { Reveal, Overline, BookButton, EASE } from "@/components/bits";
import { CONDITIONS, THERAPISTS, IMAGES } from "@/lib/site";

const lineReveal = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.25 + i * 0.15, ease: EASE },
  }),
};

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative h-[100svh] min-h-[620px] overflow-hidden flex items-center justify-center">
      <motion.div style={{ y }} className="absolute inset-0 scale-110">
        <img
          src={IMAGES.hero}
          alt="Mann får osteopatisk behandling av nakke og skuldre hos Actus Osteopati i Oslo sentrum"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="absolute inset-0 bg-olive/60" />

      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.1 }}
          className="text-sand/80 text-xs tracking-[0.35em] uppercase mb-8"
        >
          Actus Osteopati — Lilletorget 1
        </motion.p>

        <h1 className="font-display font-thin uppercase text-sand leading-[1.1] tracking-tight text-[clamp(1.1rem,4.5vw,3.5rem)] whitespace-nowrap">
          <span className="block overflow-hidden pb-1">
            <motion.span custom={0} variants={lineReveal} initial="hidden" animate="visible" className="block">
              Osteopatiklinikk | Oslo sentrum
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
          className="mt-8 text-sand/90 text-base md:text-lg leading-relaxed max-w-2xl mx-auto"
        >
          Helhetlig behandling av muskel- og skjelettplager, nakke- og
          ryggsmerter, hodepine, svimmelhet, idrettsskader og mer.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05, ease: EASE }}
          className="mt-10"
        >
          <BookButton testId="hero-book-button" label="Book time nå" />
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 w-px h-14 bg-gradient-to-b from-transparent via-sand/50 to-transparent"
      />
    </section>
  );
}

function Intro() {
  return (
    <section data-testid="intro-section" className="bg-beige py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Overline>Velkommen</Overline>
          <h2 className="font-display font-light text-2xl md:text-3xl lg:text-4xl leading-tight">
            En klinikk midt i Oslo sentrum
          </h2>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-ink-soft">
            Actus Osteopati holder til på Lilletorget 1, midt i Oslo sentrum.
            Vi tilbyr osteopatisk undersøkelse og behandling i trygge og
            profesjonelle omgivelser. Hos oss møter du behandlere som tar seg
            tid til å lytte, undersøke grundig og tilpasse behandlingen til
            deg.
          </p>
        </Reveal>

      </div>
    </section>
  );
}

function Conditions() {
  return (
    <section data-testid="conditions-section" className="bg-paper py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="text-center">
          <Overline>Hva kan vi hjelpe deg med?</Overline>
          <h2 className="font-display font-light text-2xl md:text-3xl lg:text-4xl leading-tight max-w-3xl mx-auto">
            Vi undersøker og behandler blant annet
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-2 gap-x-16 max-w-4xl mx-auto">
          {CONDITIONS.map((c, i) => (
            <Reveal key={c} delay={i * 0.04}>
              <div className="group flex items-center gap-6 py-5 border-t border-ink/10">
                <span className="font-display font-light text-xl md:text-2xl transition-transform duration-300 group-hover:translate-x-2">{c}</span>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-16 flex flex-col items-center gap-8 text-center border-t border-ink/10 pt-10 max-w-4xl mx-auto">
            <p className="text-ink-soft text-base max-w-xl">
              Usikker på om osteopati passer for deg? Ta gjerne kontakt med oss,
              eller book en time for undersøkelse og vurdering.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

function Therapists() {
  return (
    <section data-testid="therapists-section" className="bg-sand py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal className="text-center">
          <Overline>Behandlere</Overline>
          <h2 className="font-display font-light text-2xl md:text-3xl lg:text-4xl leading-tight">
            Møt våre behandlere
          </h2>
        </Reveal>
        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
          {THERAPISTS.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <article data-testid={`therapist-card-${t.name.split(" ")[0].toLowerCase()}`} className="group">
                <div className="aspect-[3/4] overflow-hidden bg-cream">
                  <img
                    src={t.photo}
                    alt={`${t.name}, osteopat ved Actus Osteopati i Oslo sentrum`}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <h3 className="mt-6 font-display font-normal text-2xl">{t.name}</h3>
                <p className="mt-1 text-xs tracking-[0.25em] uppercase text-ink-soft/70">{t.title}</p>
                {t.bio ? (
                  <p className="mt-4 text-sm text-ink-soft leading-relaxed">{t.bio[0]}</p>
                ) : (
                  <p className="mt-4 text-sm text-ink-soft/70 italic">Presentasjon og interesseområder legges inn.</p>
                )}
                <div className="mt-6 flex items-center gap-6">
                  <Link
                    data-testid={`therapist-read-more-${t.name.split(" ")[0].toLowerCase()}`}
                    to="/om-oss"
                    className="text-xs tracking-[0.2em] uppercase text-ink underline underline-offset-4 decoration-ink/30 hover:decoration-ink transition-colors duration-300"
                  >
                    Les mer
                  </Link>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Seo
        title="Actus Osteopati | Osteopat i Oslo sentrum"
        description="Osteopatiklinikk på Lilletorget 1 i Oslo sentrum. Helhetlig behandling av muskel- og skjelettplager, nakke- og ryggsmerter, hodepine, svimmelhet og idrettsskader. Book time i dag."
      />
      <Hero />
      <Intro />
      <Conditions />
      <Therapists />
    </>
  );
}
